import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ErrorApi } from "../lib/api";
import { useRefrescarDatos } from "../lib/consultas";
import {
  diasHasta,
  ETIQUETA_PAGO,
  creditos,
  formatearCantidad,
  gramos,
  mgAGramos,
  sufijoUnidad,
  UNIDAD_CREDITO,
} from "../lib/formato";
import {
  Aviso,
  BarraLimite,
  Cabecera,
  Campo,
  Cargando,
  EstadoSocio,
  FotoSocio,
  Modal,
  Panel,
  Vacio,
} from "../componentes/piezas";
import BuscadorSocios, {
  type SocioResumen,
} from "../componentes/BuscadorSocios";
import DialogoCobro from "../componentes/DialogoCobro";
import DialogoRecarga from "../componentes/DialogoRecarga";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";
import { useConexion } from "../lib/conexion";
import {
  encolar,
  nuevaClave,
  preciosEnPaquete,
  prepararEnPaquete,
  productosEnPaquete,
  refrescarPaquete,
  useSinConexion,
  buscarEnPaquete,
} from "../lib/sinConexion";

interface Producto {
  id: string;
  nombre: string;
  genetica: string;
  precioCentimos: number;
  stockMg: number;
  thc: number | null;
  cbd: number | null;
  categoria: {
    id: string;
    nombre: string;
    color: string;
    unidadMedida: string;
  };
}

interface Preparacion {
  socio: {
    id: string;
    numeroSocio: number;
    nombre: string;
    apellidos: string;
    estado: string;
    saldoCentimos: number;
    fotoUrl: string | null;
    fechaCaducidad: string | null;
    notas: string | null;
    tipoSocio: {
      id: string;
      nombre: string;
      color: string;
      cuotaAnualCentimos: number | null;
      descuentoPorcentaje: number;
    } | null;
  };
  consumo: {
    limiteDiarioMg: number;
    limiteMensualMg: number;
    consumidoHoyMg: number;
    consumidoMesMg: number;
    disponibleMg: number;
  };
  bloqueo: string | null;
  /** Si ha fichado la entrada hoy. */
  dentro: boolean;
}

interface ReservaAbierta {
  id: string;
  numero: number;
  estado: "PENDIENTE" | "PREPARADA";
  totalMg: number;
  hayGenero: boolean;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string };
  lineas: {
    id: string;
    productoId: string;
    cantidadMg: number;
    producto: { nombre: string };
  }[];
}

interface Linea {
  producto: Producto;
  gramosPedidos: number;
}

interface Precio {
  precioCentimos: number;
  precioBaseCentimos: number;
  descuentoTexto: string | null;
}

/**
 * Lo que se acaba de vender, copiado de la pantalla en el momento de cobrar.
 *
 * Antes aqui salia el ticket, y el ticket se pedia al servidor: dos consultas
 * mas —la entrega entera y los datos del club— y un «Preparando el ticket...»
 * mientras el siguiente socio esperaba de pie. Se cobra y se cierra decenas de
 * veces al dia, asi que ese retraso es el que mas se nota de todo el programa.
 *
 * Esto no pide nada: son los mismos numeros que ya estaban en el carrito, asi
 * que la pantalla aparece hecha. Se guarda antes de vaciar el carrito.
 */
interface VentaCerrada {
  /** Null cuando se hizo sin red: el numero se lo pone el servidor al recibirla. */
  numero: number | null;
  socio: string;
  metodoPago: string;
  totalCentimos: number;
  ahorroCentimos: number;
  totalMg: number;
  totalUnidades: number;
  /** Lo que le queda en la cartera. Solo cuando ha pagado con ella. */
  saldoRestanteCentimos: number | null;
  /** Lo rebajado a mano, si se rebajo algo. */
  descuentoManualCentimos: number;
  /** La cantidad va en la unidad interna (mg o milesimas de unidad), como en todo el programa. */
  lineas: {
    nombre: string;
    cantidadMg: number;
    unidad: string;
    importeCentimos: number;
  }[];
}

/** Un descuento puesto a mano, tal y como se esta escribiendo en la pantalla. */
interface DescuentoManual {
  tipo: "PORCENTAJE" | "EUROS";
  /** Texto, no numero: hace falta poder dejarlo a medias mientras se teclea. */
  valor: string;
}

/**
 * Lo que se quita del total, calculado igual que en el servidor.
 *
 * Es el mismo calculo escrito dos veces, y a proposito: el boton dice «Registrar
 * entrega - 17,00 EUR» antes de mandar nada, y si aqui saliera otro numero el
 * socio pagaria algo distinto de lo que se le acaba de decir. El servidor manda
 * -es el que tiene los precios-, pero la pantalla no puede prometer otra cosa.
 */
function descuentoDe(
  totalCentimos: number,
  descuento: DescuentoManual | null,
): number {
  if (!descuento || totalCentimos <= 0) return 0;
  // Aqui se escribe «5,50», no «5.50».
  const valor = Number(descuento.valor.replace(",", "."));
  if (!Number.isFinite(valor) || valor <= 0) return 0;

  if (descuento.tipo === "PORCENTAJE") {
    return Math.min(
      totalCentimos,
      Math.round((totalCentimos * Math.min(100, valor)) / 100),
    );
  }
  return Math.min(totalCentimos, Math.round(valor * 100));
}

const CANTIDADES_RAPIDAS = [0.5, 1, 2, 3, 5, 10];

export default function Mostrador() {
  const [recargando, setRecargando] = useState(false);
  /*
   * A quien ya se le ha preguntado por el fichaje. Sin esto, decir «no» volveria
   * a sacar el aviso en cuanto la pantalla se refresque, y quien atiende
   * tendria que pelearse con un cartel que no se va.
   */
  const [fichajeResuelto, setFichajeResuelto] = useState<string | null>(null);
  const [fichando, setFichando] = useState(false);
  const refrescarDatos = useRefrescarDatos();
  const { socioId } = useParams();
  const navegar = useNavigate();
  const { puede, club, usuario } = useSesion();
  const { t, dias: enDias, delServidor, delFallo } = useIdioma();

  const [socioElegido, setSocioElegido] = useState<string | null>(
    socioId ?? null,
  );
  const [carrito, setCarrito] = useState<Linea[]>([]);
  /** Si esta entrega viene de una reserva del portal, para cerrarla al cobrar. */
  const [reservaServida, setReservaServida] = useState<string | null>(null);
  const [categoria, setCategoria] = useState("");
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  /** El pedido que se esta cancelando, mientras el cuadro pide el motivo. */
  const [cancelando, setCancelando] = useState<ReservaAbierta | null>(null);
  /** Se perdio la conexion al registrar: no se sabe si la entrega entro. */
  const [dudaDeGuardado, setDudaDeGuardado] = useState(false);
  const [avisoLimite, setAvisoLimite] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [venta, setVenta] = useState<VentaCerrada | null>(null);
  const [descuento, setDescuento] = useState<DescuentoManual | null>(null);
  const [cobrandoCuota, setCobrandoCuota] = useState(false);

  useEffect(() => setSocioElegido(socioId ?? null), [socioId]);

  /*
   * Sin red, el mostrador sigue con el paquete guardado en la tablet: busca
   * socios, calcula limites y precios y apunta las entregas en cola. Ver
   * lib/sinConexion.ts. Mientras hay red, el paquete se refresca al abrir y
   * cada cinco minutos, para que lo guardado tenga minutos, no dias.
   */
  const { enLinea } = useConexion();
  const { paquete, cola } = useSinConexion();
  const modoLocal = !enLinea && paquete !== null;
  useEffect(() => {
    if (!enLinea) return;
    void refrescarPaquete();
    const cada = setInterval(() => void refrescarPaquete(), 5 * 60_000);
    return () => clearInterval(cada);
  }, [enLinea]);

  const consultaProductos = useQuery({
    queryKey: ["productos-mostrador"],
    queryFn: () =>
      api<Producto[]>("/catalogo/productos", { query: { soloActivos: true } }),
    enabled: enLinea,
  });
  const productos: {
    data: Producto[] | undefined;
    isLoading: boolean;
    refetch: () => unknown;
  } = modoLocal
    ? {
        data: productosEnPaquete(paquete, cola),
        isLoading: false,
        refetch: () => undefined,
      }
    : consultaProductos;

  const reservas = useQuery({
    queryKey: ["reservas-abiertas"],
    queryFn: () => api<ReservaAbierta[]>("/reservas"),
    enabled: enLinea,
  });

  /*
   * Cuanto hay ya reservado de cada producto.
   *
   * Hace falta porque reservar NO retiene genero: el aviso de «falta genero» de
   * la tabla compara cada linea contra el stock entero, sin descontar lo que ya
   * tienen pedido los demas, asi que dos socios pueden reservar los mismos
   * ultimos 10 g y a los dos les sale que si hay. Con esto a la vista, el del
   * mostrador lo ve antes de que aparezca nadie.
   */
  const reservado = useQuery({
    queryKey: ["reservado-por-producto"],
    queryFn: () => api<{ productoId: string; reservadoMg: number }[]>("/reservas/pendiente-por-producto"),
    enabled: enLinea,
  });

  const reservadoDe = (productoId: string) =>
    (reservado.data ?? []).find((r) => r.productoId === productoId)?.reservadoMg ?? 0;

  /**
   * Carga una reserva en la pantalla: elige al socio y llena la entrega con lo
   * que pidio. A partir de ahí el mostrador puede corregir lo que haga falta,
   * porque lo que se lleva de verdad manda sobre lo que se pidio.
   */
  function servirReserva(reserva: ReservaAbierta) {
    setOk("");
    const lineas: Linea[] = [];
    for (const linea of reserva.lineas) {
      const producto = (productos.data ?? []).find(
        (p) => p.id === linea.productoId,
      );
      if (producto)
        lineas.push({ producto, gramosPedidos: linea.cantidadMg / 1000 });
    }
    setSocioElegido(reserva.socio.id);
    setCarrito(lineas);
    setReservaServida(reserva.id);
    // Reservo porque tenía saldo, así que se cobra de la cartera. El mostrador
    // puede cambiarlo si el socio prefiere pagar de otra forma.
    setMetodoPago("CARTERA");
    navegar(`/mostrador/${reserva.socio.id}`, { replace: true });
  }

  const consultaPreparacion = useQuery({
    queryKey: ["preparar", socioElegido],
    queryFn: () => api<Preparacion>(`/dispensaciones/preparar/${socioElegido}`),
    enabled: Boolean(socioElegido) && enLinea,
  });
  const preparacion: {
    data: Preparacion | undefined;
    isLoading: boolean;
    refetch: () => unknown;
  } =
    modoLocal && socioElegido
      ? {
          data: prepararEnPaquete(paquete, socioElegido, cola) ?? undefined,
          isLoading: false,
          refetch: () => undefined,
        }
      : consultaPreparacion;

  // Precios de este socio ahora mismo (tipo y descuentos activos). Se refresca
  // cada minuto por si entra o sale una franja horaria.
  const consultaPrecios = useQuery({
    queryKey: ["precios", socioElegido],
    queryFn: () =>
      api<Record<string, Precio>>(`/dispensaciones/precios/${socioElegido}`),
    enabled: Boolean(socioElegido) && enLinea,
    refetchInterval: 60_000,
  });
  const precios: { data: Record<string, Precio> | undefined } =
    modoLocal && socioElegido
      ? { data: preciosEnPaquete(paquete, socioElegido) }
      : consultaPrecios;
  const precioDe = (producto: Producto): Precio =>
    precios.data?.[producto.id] ?? {
      precioCentimos: producto.precioCentimos,
      precioBaseCentimos: producto.precioCentimos,
      descuentoTexto: null,
    };

  const categorias = useMemo(() => {
    const vistas = new Map<
      string,
      { id: string; nombre: string; color: string }
    >();
    for (const p of productos.data ?? [])
      vistas.set(p.categoria.id, p.categoria);
    return [...vistas.values()];
  }, [productos.data]);

  const visibles = (productos.data ?? []).filter(
    (p) => !categoria || p.categoria.id === categoria,
  );

  // Igual que en la API: al límite solo le afecta lo que se pesa.
  const esUnidad = (l: Linea) => l.producto.categoria.unidadMedida === "UNIDAD";
  const totalMg = carrito
    .filter((l) => !esUnidad(l))
    .reduce((s, l) => s + Math.round(l.gramosPedidos * 1000), 0);
  const totalUnidades = carrito
    .filter(esUnidad)
    .reduce((s, l) => s + l.gramosPedidos, 0);
  const ultimaLinea = carrito[carrito.length - 1];
  const rapidas =
    ultimaLinea && esUnidad(ultimaLinea)
      ? [1, 2, 3, 4, 5, 6]
      : CANTIDADES_RAPIDAS;
  const importeLinea = (l: Linea) =>
    Math.round(
      (precioDe(l.producto).precioCentimos *
        Math.round(l.gramosPedidos * 1000)) /
        1000,
    );
  const totalDeTarifaCentimos = carrito.reduce(
    (s, l) => s + importeLinea(l),
    0,
  );
  /*
   * La rebaja de mano va sobre el precio que ya lleva la tarifa del socio y las
   * campañas, igual que en el servidor. A partir de aqui, totalCentimos es lo
   * que se cobra de verdad: el saldo de la cartera, el boton y la caja miran
   * todos a ese numero.
   */
  const descuentoManualCentimos = descuentoDe(totalDeTarifaCentimos, descuento);
  const totalCentimos = totalDeTarifaCentimos - descuentoManualCentimos;
  const totalBaseCentimos = carrito.reduce(
    (s, l) =>
      s +
      Math.round(
        (precioDe(l.producto).precioBaseCentimos *
          Math.round(l.gramosPedidos * 1000)) /
          1000,
      ),
    0,
  );
  /*
   * El ahorro de tarifa se mide contra el total ANTES de la rebaja de mano.
   * Con totalCentimos aqui, una rebaja se contaba dos veces: como ahorro y como
   * descuento, y la pantalla decia que el socio se ahorraba mas de lo que se
   * ahorraba.
   */
  const ahorroCentimos = totalBaseCentimos - totalDeTarifaCentimos;

  const consumo = preparacion.data?.consumo;
  const pasaLimite = consumo ? totalMg > consumo.disponibleMg : false;
  const saldoInsuficiente =
    metodoPago === "CARTERA" && preparacion.data
      ? preparacion.data.socio.saldoCentimos < totalCentimos
      : false;

  function anadir(producto: Producto) {
    setCarrito((actual) => {
      const existente = actual.find((l) => l.producto.id === producto.id);
      if (existente) {
        return actual.map((l) =>
          l.producto.id === producto.id
            ? { ...l, gramosPedidos: Number((l.gramosPedidos + 1).toFixed(2)) }
            : l,
        );
      }
      return [...actual, { producto, gramosPedidos: 1 }];
    });
  }

  function cambiarCantidad(productoId: string, valor: number) {
    setCarrito((actual) =>
      actual.map((l) =>
        l.producto.id === productoId ? { ...l, gramosPedidos: valor } : l,
      ),
    );
  }

  function quitar(productoId: string) {
    setCarrito((actual) => actual.filter((l) => l.producto.id !== productoId));
  }

  /** El resumen que se enseña al cerrar, copiado de la pantalla antes de vaciar el carrito. */
  function resumenDeLaVenta(numero: number | null): VentaCerrada {
    const socio = preparacion.data?.socio;
    return {
      numero,
      socio: socio ? `${socio.nombre} ${socio.apellidos}` : "",
      metodoPago,
      totalCentimos,
      ahorroCentimos,
      totalMg,
      totalUnidades,
      saldoRestanteCentimos:
        metodoPago === "CARTERA" && socio
          ? socio.saldoCentimos - totalCentimos
          : null,
      descuentoManualCentimos,
      lineas: carrito.map((l) => ({
        nombre: l.producto.nombre,
        cantidadMg: Math.round(l.gramosPedidos * 1000),
        unidad: l.producto.categoria.unidadMedida,
        importeCentimos: importeLinea(l),
      })),
    };
  }

  function vaciarTrasVender() {
    setCarrito([]);
    setDescuento(null);
    setReservaServida(null);
    setAvisoLimite(null);
  }

  const peticionDeDescuento =
    descuentoManualCentimos > 0 && descuento
      ? {
          tipo: descuento.tipo,
          valor: Number(descuento.valor.replace(",", ".")),
        }
      : null;

  /**
   * Sin red: la entrega va a la cola de la tablet y se envia sola al volver
   * la conexion. Las mismas reglas que con red -limite y autorizacion- pero
   * calculadas con el paquete; el servidor las vuelve a mirar al recibirla y
   * apunta lo que se salto.
   */
  function apuntarEnCola(forzarLimite: boolean) {
    if (!socioElegido || carrito.length === 0 || !consumo) return;
    if (pasaLimite && !forzarLimite) {
      setAvisoLimite(
        t("m.superaLimite", {
          disponible: gramos(consumo.disponibleMg),
          pedido: gramos(totalMg),
        }),
      );
      return;
    }
    if (pasaLimite && usuario?.rol !== "PROPIETARIO") {
      setError(t("m.soloPropietario"));
      return;
    }
    const resumen = resumenDeLaVenta(null);
    encolar({
      hechaPor: usuario ? { usuarioId: usuario.id, nombre: usuario.nombre } : null,
      socioId: socioElegido,
      socioNombre: resumen.socio,
      numeroSocio: preparacion.data?.socio.numeroSocio ?? 0,
      lineas: carrito.map((l) => ({
        productoId: l.producto.id,
        nombre: l.producto.nombre,
        cantidadG: l.gramosPedidos,
        unidad: l.producto.categoria.unidadMedida,
      })),
      metodoPago,
      forzarLimite,
      descuentoManual: peticionDeDescuento,
      totalCentimosPrevisto: totalCentimos,
      totalMg,
    });
    setVenta(resumen);
    vaciarTrasVender();
  }

  async function registrar(forzarLimite = false) {
    if (!socioElegido || carrito.length === 0) return;
    setError("");
    // El cartel de la cancelacion anterior no puede seguir puesto mientras se
    // atiende al siguiente: diria algo que ya no es de lo que hay en pantalla.
    setOk("");
    setDudaDeGuardado(false);
    if (modoLocal) {
      apuntarEnCola(forzarLimite);
      return;
    }
    setEnviando(true);
    /*
     * La clave va en todas las entregas. Si la red se corta entre mandar y
     * recibir la respuesta, se guarda en la cola con esta misma clave y se
     * reenvia al volver: el servidor sabe que es la misma y no la duplica.
     */
    const claveLocal = nuevaClave();
    try {
      const respuesta = await api<{
        dispensacion: { id: string; numero: number };
      }>("/dispensaciones", {
        metodo: "POST",
        cuerpo: {
          socioId: socioElegido,
          reservaId: reservaServida,
          metodoPago,
          forzarLimite,
          claveLocal,
          // Se manda lo que se pidio, no el importe: el precio lo pone el
          // servidor, y el descuento se aplica alli sobre su propio total.
          descuentoManual: peticionDeDescuento,
          lineas: carrito.map((l) => ({
            productoId: l.producto.id,
            cantidadG: l.gramosPedidos,
          })),
        },
      });
      // El resumen se arma antes de vaciar el carrito: despues ya no hay nada
      // que copiar, y pedirselo al servidor es justo lo que hacia esperar.
      const socio = preparacion.data?.socio;
      setVenta({
        numero: respuesta.dispensacion.numero,
        socio: socio ? `${socio.nombre} ${socio.apellidos}` : "",
        metodoPago,
        totalCentimos,
        ahorroCentimos,
        totalMg,
        totalUnidades,
        saldoRestanteCentimos:
          metodoPago === "CARTERA" && socio
            ? socio.saldoCentimos - totalCentimos
            : null,
        descuentoManualCentimos,
        lineas: carrito.map((l) => ({
          nombre: l.producto.nombre,
          cantidadMg: Math.round(l.gramosPedidos * 1000),
          unidad: l.producto.categoria.unidadMedida,
          importeCentimos: importeLinea(l),
        })),
      });
      vaciarTrasVender();
      void preparacion.refetch();
      void productos.refetch();
      void refrescarPaquete();
      // Una entrega mueve el panel de inicio, la caja, el historial del socio
      // y el stock: todo eso se pide de nuevo cuando se vaya a mirar.
      refrescarDatos();
    } catch (fallo) {
      if (
        fallo instanceof ErrorApi &&
        (fallo.detalles as { requiereAutorizacion?: boolean })
          ?.requiereAutorizacion
      ) {
        setAvisoLimite(fallo.message);
      } else if (fallo instanceof ErrorApi && fallo.status === 0 && paquete) {
        /*
         * Se corto la red al mandarla. Antes esto era «no se sabe si entro,
         * compruebalo antes de repetir». Ahora va a la cola con la misma
         * clave: si entro, el servidor la reconoce y no la duplica; si no,
         * entra al volver la red. En los dos casos, el genero ya se entrego.
         */
        const resumen = resumenDeLaVenta(null);
        encolar({
          hechaPor: usuario ? { usuarioId: usuario.id, nombre: usuario.nombre } : null,
          socioId: socioElegido,
          socioNombre: resumen.socio,
          numeroSocio: preparacion.data?.socio.numeroSocio ?? 0,
          lineas: carrito.map((l) => ({
            productoId: l.producto.id,
            nombre: l.producto.nombre,
            cantidadG: l.gramosPedidos,
            unidad: l.producto.categoria.unidadMedida,
          })),
          metodoPago,
          forzarLimite,
          descuentoManual: peticionDeDescuento,
          totalCentimosPrevisto: totalCentimos,
          totalMg,
          clave: claveLocal,
        });
        setVenta(resumen);
        vaciarTrasVender();
      } else {
        setError(
          fallo instanceof Error ? fallo.message : t("m.falloRegistrar"),
        );
        /*
         * Si se corto la red y no hay paquete con el que seguir, la entrega
         * pudo guardarse igual. Repetirla cobraria dos veces, asi que hay que
         * llevar a comprobarlo antes de que nadie toque el boton otra vez.
         */
        setDudaDeGuardado(fallo instanceof ErrorApi && fallo.ambiguo);
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Cabecera titulo={t("m.titulo")} descripcion={t("m.descripcion")} />

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{ok}</Aviso>

      {dudaDeGuardado && (
        <Aviso tipo="atencion">
          {t("m.dudaDeGuardado1")}{" "}
          <Link to="/dispensaciones">{t("m.dispensaciones")}</Link>{" "}
          {t("m.dudaDeGuardado2")}
        </Aviso>
      )}

      {!socioElegido && reservas.data && reservas.data.length > 0 && (
        <Panel titulo={t("m.reservas")} sinCuerpo>
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("m.socio")}</th>
                  <th>{t("m.pedido")}</th>
                  <th className="num">{t("m.total")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {reservas.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>
                        #{r.socio.numeroSocio} {r.socio.nombre}{" "}
                        {r.socio.apellidos}
                      </strong>
                      <div className="suave pequeno">
                        {t("m.reservaNumero", { numero: r.numero })}
                      </div>
                    </td>
                    <td className="pequeno">
                      {r.lineas.map((l) => (
                        <div key={l.id}>
                          {l.producto.nombre} · {gramos(l.cantidadMg)}
                        </div>
                      ))}
                    </td>
                    <td className="num tabular">
                      {gramos(r.totalMg)}
                      {!r.hayGenero && (
                        <div className="etiqueta roja">
                          {t("m.faltaGenero")}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="fila">
                        <button
                          className="boton"
                          onClick={() => servirReserva(r)}
                        >
                          {t("m.atender")}
                        </button>
                        {/* Secundario a proposito: atender es lo que se hace
                            casi siempre, y cancelar es la salida para cuando
                            no hay genero. */}
                        <button
                          className="boton secundario"
                          onClick={() => setCancelando(r)}
                        >
                          {t("m.cancelarReserva")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {cancelando && (
        <DialogoCancelarPedido
          reserva={cancelando}
          alCerrar={() => setCancelando(null)}
          alHecho={() => {
            setCancelando(null);
            setError("");
            setOk(t("m.can.hecho"));
            refrescarDatos();
          }}
          alFallar={(mensaje) => {
            // Sin cerrar el cuadro: si se cierra, el motivo escrito se va con el
            // y hay que volver a redactarlo. Es el patron de Donaciones.
            setOk("");
            setError(mensaje);
          }}
        />
      )}

      {!socioElegido ? (
        <Panel titulo={t("m.aQuien")}>
          <BuscadorSocios
            buscarLocal={
              paquete ? (texto) => buscarEnPaquete(paquete, texto) : undefined
            }
            autoFoco
            alElegir={(socio: SocioResumen) => {
              setSocioElegido(socio.id);
              navegar(`/mostrador/${socio.id}`, { replace: true });
            }}
          />
          <p
            className="suave pequeno"
            style={{ marginBottom: 0, marginTop: 12 }}
          >
            {t("m.comoBuscar")}
          </p>
        </Panel>
      ) : preparacion.isLoading || !preparacion.data ? (
        <Cargando />
      ) : (
        <div className="mostrador">
          {/* --- Catalogo --- */}
          <Panel sinCuerpo>
            <div
              className="panel-cabecera"
              style={{ gap: 6, flexWrap: "wrap" }}
            >
              <button
                className={`boton ${categoria === "" ? "" : "secundario"}`}
                onClick={() => setCategoria("")}
              >
                {t("m.todo")}
              </button>
              {categorias.map((c) => (
                <button
                  key={c.id}
                  className={`boton ${categoria === c.id ? "" : "secundario"}`}
                  onClick={() => setCategoria(c.id)}
                >
                  {c.nombre}
                </button>
              ))}
            </div>

            <div className="panel-cuerpo">
              {productos.isLoading ? (
                <Cargando />
              ) : visibles.length === 0 ? (
                <Vacio>{t("m.sinProductos")}</Vacio>
              ) : (
                <div className="rejilla-productos">
                  {visibles.map((producto) => (
                    <button
                      key={producto.id}
                      className="tarjeta-producto"
                      disabled={producto.stockMg <= 0}
                      onClick={() => anadir(producto)}
                    >
                      <div
                        className="cinta"
                        style={{ background: producto.categoria.color }}
                      />
                      <span className="nombre">{producto.nombre}</span>
                      <span className="suave pequeno">
                        {producto.thc
                          ? `THC ${producto.thc}%`
                          : producto.categoria.nombre}
                      </span>
                      {precioDe(producto).descuentoTexto && (
                        <span
                          className="etiqueta verde"
                          style={{ alignSelf: "flex-start" }}
                        >
                          {precioDe(producto).descuentoTexto}
                        </span>
                      )}
                      <div className="fila separada" style={{ marginTop: 4 }}>
                        <strong className="tabular">
                          {precioDe(producto).descuentoTexto && (
                            <span
                              className="suave"
                              style={{
                                textDecoration: "line-through",
                                fontWeight: 400,
                                marginRight: 4,
                              }}
                            >
                              {creditos(precioDe(producto).precioBaseCentimos)}
                            </span>
                          )}
                          {creditos(precioDe(producto).precioCentimos)}/
                          {sufijoUnidad(producto.categoria.unidadMedida)}
                        </strong>
                        <div
                          className="columna"
                          style={{ gap: 0, alignItems: "flex-end" }}
                        >
                          <span
                            className={`pequeno tabular ${producto.stockMg <= 0 ? "" : "suave"}`}
                            style={
                              producto.stockMg <= 0
                                ? { color: "var(--rojo)" }
                                : undefined
                            }
                          >
                            {producto.stockMg <= 0
                              ? t("m.sinStock")
                              : formatearCantidad(
                                  producto.stockMg,
                                  producto.categoria.unidadMedida,
                                  producto.stockMg < 10000 ? 1 : 0,
                                )}
                          </span>
                          {/* Lo que ya tienen pedido otros. En rojo cuando pasa de
                              lo que queda, que es cuando alguien se va a quedar
                              sin lo suyo. */}
                          {reservadoDe(producto.id) > 0 && (
                            <span
                              className={`pequeno tabular ${
                                reservadoDe(producto.id) > producto.stockMg ? "" : "suave"
                              }`}
                              style={
                                reservadoDe(producto.id) > producto.stockMg
                                  ? { color: "var(--rojo)" }
                                  : undefined
                              }
                            >
                              {t("m.reservado", {
                                cantidad: formatearCantidad(
                                  reservadoDe(producto.id),
                                  producto.categoria.unidadMedida,
                                  reservadoDe(producto.id) < 10000 ? 1 : 0,
                                ),
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Panel>

          {/* --- Socio y carrito --- */}
          <div className="columna" style={{ gap: 16 }}>
            <Panel>
              <div className="fila separada">
                <div className="fila">
                  <FotoSocio socio={preparacion.data.socio} />
                  <div>
                    <strong>
                      {preparacion.data.socio.nombre}{" "}
                      {preparacion.data.socio.apellidos}
                    </strong>
                    <div className="fila pequeno" style={{ gap: 6 }}>
                      <span className="suave">
                        #{preparacion.data.socio.numeroSocio}
                      </span>
                      <EstadoSocio estado={preparacion.data.socio.estado} />
                      {preparacion.data.socio.tipoSocio && (
                        <span
                          className="etiqueta"
                          style={{
                            background: `${preparacion.data.socio.tipoSocio.color}20`,
                            color: preparacion.data.socio.tipoSocio.color,
                          }}
                        >
                          {preparacion.data.socio.tipoSocio.nombre}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  className="boton fantasma"
                  onClick={() => {
                    setSocioElegido(null);
                    setCarrito([]);
                    setReservaServida(null);
                    navegar("/mostrador", { replace: true });
                  }}
                >
                  Cambiar
                </button>
              </div>

              {preparacion.data.bloqueo && (
                <div style={{ marginTop: 12 }}>
                  <Aviso tipo="error">
                    {delServidor(preparacion.data.bloqueo)}
                  </Aviso>
                </div>
              )}

              <div className="columna" style={{ gap: 12, marginTop: 14 }}>
                <BarraLimite
                  consumidoMg={preparacion.data.consumo.consumidoHoyMg}
                  limiteMg={preparacion.data.consumo.limiteDiarioMg}
                  etiqueta={t("m.hoy")}
                />
                <BarraLimite
                  consumidoMg={preparacion.data.consumo.consumidoMesMg}
                  limiteMg={preparacion.data.consumo.limiteMensualMg}
                  etiqueta={t("m.esteMes")}
                />
              </div>

              <div className="fila separada" style={{ marginTop: 12 }}>
                <span className="suave pequeno">{t("m.cartera")}</span>
                <div className="fila" style={{ gap: 8 }}>
                  <strong className="tabular">
                    {creditos(preparacion.data.socio.saldoCentimos)}
                  </strong>
                  {puede("recargarCartera") && (
                    <button
                      className="boton fantasma"
                      title={t("m.ingresarTitulo")}
                      onClick={() => setRecargando(true)}
                    >
                      {t("m.ingresar")}
                    </button>
                  )}
                </div>
              </div>

              {preparacion.data.socio.notas && (
                <div style={{ marginTop: 12 }}>
                  <Aviso tipo="atencion">
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {preparacion.data.socio.notas}
                    </span>
                  </Aviso>
                </div>
              )}

              {(() => {
                const dias = diasHasta(preparacion.data.socio.fechaCaducidad);
                if (dias === null || dias > 30) return null;
                return (
                  <div style={{ marginTop: 12 }}>
                    <Aviso tipo={dias < 0 ? "error" : "atencion"}>
                      {dias < 0
                        ? t("m.cuotaVencioHace", { dias: enDias(-dias) })
                        : dias === 0
                          ? t("m.cuotaVenceHoy")
                          : t("m.cuotaVenceEn", { dias: enDias(dias) })}{" "}
                      {puede("cobrarCuota") && (
                        <button
                          className="boton fantasma"
                          onClick={() => setCobrandoCuota(true)}
                        >
                          {t("m.cobrarAhora")}
                        </button>
                      )}
                    </Aviso>
                  </div>
                );
              })()}
            </Panel>

            <Panel titulo={t("m.entrega")} id="entrega">
              {carrito.length === 0 ? (
                <Vacio>{t("m.tocaProducto")}</Vacio>
              ) : (
                <>
                  {carrito.map((linea) => (
                    <div key={linea.producto.id} className="carrito-linea">
                      <div>
                        <strong>{linea.producto.nombre}</strong>
                        <div className="suave pequeno tabular">
                          {creditos(precioDe(linea.producto).precioCentimos)}/
                          {sufijoUnidad(linea.producto.categoria.unidadMedida)}{" "}
                          · {creditos(importeLinea(linea))}
                          {precioDe(linea.producto).descuentoTexto &&
                            ` · ${precioDe(linea.producto).descuentoTexto}`}
                        </div>
                      </div>
                      <div className="fila">
                        <input
                          type="number"
                          min={esUnidad(linea) ? 1 : 0.1}
                          step={esUnidad(linea) ? 1 : 0.1}
                          max={mgAGramos(linea.producto.stockMg)}
                          value={linea.gramosPedidos}
                          onChange={(e) =>
                            cambiarCantidad(
                              linea.producto.id,
                              Number(e.target.value),
                            )
                          }
                        />
                        <span className="suave pequeno">
                          {sufijoUnidad(linea.producto.categoria.unidadMedida)}
                        </span>
                        <button
                          className="boton fantasma"
                          onClick={() => quitar(linea.producto.id)}
                          aria-label={t("m.quitar")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="teclado" style={{ marginTop: 12 }}>
                    {rapidas.map((cantidad) => (
                      <button
                        key={cantidad}
                        onClick={() => {
                          if (ultimaLinea)
                            cambiarCantidad(ultimaLinea.producto.id, cantidad);
                        }}
                      >
                        {cantidad}{" "}
                        {ultimaLinea && esUnidad(ultimaLinea) ? "ud" : "g"}
                      </button>
                    ))}
                  </div>

                  <div
                    className="fila separada"
                    style={{
                      marginTop: 16,
                      paddingTop: 12,
                      borderTop: "1px solid var(--borde)",
                    }}
                  >
                    <span>{t("m.total")}</span>
                    <div style={{ textAlign: "right" }}>
                      {descuentoManualCentimos > 0 && (
                        <div
                          className="suave pequeno tabular"
                          style={{ textDecoration: "line-through" }}
                        >
                          {creditos(totalDeTarifaCentimos)}
                        </div>
                      )}
                      <div
                        style={{ fontSize: "1.5rem", fontWeight: 680 }}
                        className="tabular"
                      >
                        {creditos(totalCentimos)}
                      </div>
                      <div className="suave pequeno tabular">
                        {gramos(totalMg)}
                        {totalUnidades > 0 && ` · ${totalUnidades} ud`}
                      </div>
                      {ahorroCentimos > 0 && (
                        <div
                          className="pequeno tabular"
                          style={{ color: "var(--verde)" }}
                        >
                          {t("m.ahorro", { importe: creditos(ahorroCentimos) })}
                        </div>
                      )}
                      {descuentoManualCentimos > 0 && (
                        <div
                          className="pequeno tabular"
                          style={{ color: "var(--verde)" }}
                        >
                          {t("m.descuentoAMano", {
                            importe: creditos(descuentoManualCentimos),
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/*
                    El descuento de mano.

                    Solo aparece si la cuenta puede hacerlo: en un mostrador no
                    conviene enseñar un boton que va a decir que no. El importe
                    se ve arriba al momento, para poder decirle el precio al
                    socio antes de darle a cobrar.
                  */}
                  {puede("descuentoManual") && (
                    <div
                      className="fila descuento-mano"
                      style={{ marginTop: 10 }}
                    >
                      {descuento ? (
                        <>
                          <input
                            type="number"
                            min={0}
                            step={descuento.tipo === "PORCENTAJE" ? 1 : 0.5}
                            max={
                              descuento.tipo === "PORCENTAJE" ? 100 : undefined
                            }
                            value={descuento.valor}
                            autoFocus
                            aria-label={t("m.descuento")}
                            placeholder="0"
                            onChange={(e) =>
                              setDescuento({
                                ...descuento,
                                valor: e.target.value,
                              })
                            }
                          />
                          <button
                            className={`boton ${descuento.tipo === "PORCENTAJE" ? "" : "secundario"}`}
                            onClick={() =>
                              setDescuento({ ...descuento, tipo: "PORCENTAJE" })
                            }
                          >
                            %
                          </button>
                          <button
                            className={`boton ${descuento.tipo === "EUROS" ? "" : "secundario"}`}
                            onClick={() =>
                              setDescuento({ ...descuento, tipo: "EUROS" })
                            }
                          >
                            {UNIDAD_CREDITO}
                          </button>
                          <button
                            className="boton fantasma"
                            aria-label={t("m.quitarDescuento")}
                            onClick={() => setDescuento(null)}
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          className="boton secundario bloque"
                          onClick={() =>
                            setDescuento({ tipo: "PORCENTAJE", valor: "" })
                          }
                        >
                          {t("m.hacerDescuento")}
                        </button>
                      )}
                    </div>
                  )}

                  {pasaLimite && (
                    <div style={{ marginTop: 12 }}>
                      <Aviso tipo="atencion">
                        {t("m.superaLimiteCorto", {
                          disponible: gramos(consumo!.disponibleMg),
                        })}
                      </Aviso>
                    </div>
                  )}
                  {saldoInsuficiente && (
                    <div style={{ marginTop: 12 }}>
                      <Aviso tipo="error">
                        La cartera del socio no tiene saldo suficiente.
                        {puede("recargarCartera") && (
                          <>
                            {" "}
                            <button
                              className="boton fantasma"
                              style={{ padding: 0 }}
                              onClick={() => setRecargando(true)}
                            >
                              Ingresar ahora
                            </button>
                          </>
                        )}
                      </Aviso>
                    </div>
                  )}

                  <div className="fila" style={{ marginTop: 12, gap: 6 }}>
                    {["EFECTIVO", "CARTERA", "TARJETA"].map((forma) => (
                      <button
                        key={forma}
                        className={`boton ${metodoPago === forma ? "" : "secundario"}`}
                        style={{ flex: 1 }}
                        onClick={() => setMetodoPago(forma)}
                      >
                        {t(`pago.${forma}` as Clave)}
                      </button>
                    ))}
                  </div>

                  <button
                    className="boton grande bloque"
                    style={{ marginTop: 12 }}
                    disabled={
                      enviando ||
                      carrito.length === 0 ||
                      Boolean(preparacion.data.bloqueo) ||
                      saldoInsuficiente ||
                      !puede("dispensar")
                    }
                    onClick={() => void registrar(false)}
                  >
                    {enviando
                      ? t("m.registrando")
                      : t("m.registrar", { total: creditos(totalCentimos) })}
                  </button>
                </>
              )}
            </Panel>
          </div>

          {/*
           * Resumen fijo del carrito, solo en pantallas estrechas.
           *
           * En una tablet el mostrador se apila —productos arriba, socio en
           * medio, entrega abajo— y el total quedaba fuera de la pantalla: en
           * cada venta habia que bajar para ver cuanto llevaba y para cobrar.
           * En un mostrador con gente esperando, eso son dos gestos de mas por
           * cliente. La barra ensena el total siempre y lleva al cobro de un
           * toque. En escritorio no aparece: alli la entrega ya se ve.
           */}
          {carrito.length > 0 && (
            <div className="resumen-carrito">
              <div>
                <strong>{creditos(totalCentimos)}</strong>
                <span className="suave pequeno">
                  {carrito.length === 1
                    ? t("m.unProducto")
                    : t("m.productos", { n: carrito.length })}
                </span>
              </div>
              <button
                className="boton"
                /* Salto instantaneo y no suave: el desplazamiento animado no
                   funciona en todos los navegadores, y en un mostrador con
                   gente esperando tampoco se echa de menos. */
                onClick={() =>
                  document
                    .getElementById("entrega")
                    ?.scrollIntoView({ block: "start" })
                }
              >
                {t("m.irACobrar")}
              </button>
            </div>
          )}
        </div>
      )}

      {cobrandoCuota && preparacion.data && (
        <DialogoCobro
          socio={preparacion.data.socio}
          importePorDefecto={
            (preparacion.data.socio.tipoSocio?.cuotaAnualCentimos ??
              club?.cuotaAnualCentimos ??
              2000) / 100
          }
          alCerrar={() => setCobrandoCuota(false)}
          alHecho={() => {
            setCobrandoCuota(false);
            setError("");
            void preparacion.refetch();
          }}
          alFallar={(mensaje) => {
            setCobrandoCuota(false);
            setError(mensaje);
          }}
        />
      )}

      {recargando && preparacion.data && (
        <DialogoRecarga
          socio={preparacion.data.socio}
          faltanCentimos={
            // Solo se propone lo que falta si estamos cobrando de la cartera;
            // si paga en efectivo, la recarga no tiene nada que ver con el carro.
            metodoPago === "CARTERA"
              ? Math.max(
                  0,
                  totalCentimos - preparacion.data.socio.saldoCentimos,
                )
              : undefined
          }
          alCerrar={() => setRecargando(false)}
          alHecho={() => {
            setRecargando(false);
            setError("");
            void preparacion.refetch();
          }}
          alFallar={(mensaje) => {
            setRecargando(false);
            setError(mensaje);
          }}
        />
      )}

      {/*
       * El socio esta en el mostrador pero no ha fichado la entrada. Pasa a
       * diario: entra con alguien, o el de la puerta estaba ocupado. Se
       * ofrece ficharle aqui en vez de mandar a nadie a la pantalla de
       * accesos, que es como el aforo acaba siendo mentira.
       */}
      {preparacion.data &&
        !modoLocal &&
        !preparacion.data.dentro &&
        !preparacion.data.bloqueo &&
        fichajeResuelto !== preparacion.data.socio.id && (
          <Modal
            titulo={t("m.sinFichar.titulo")}
            alCerrar={() => setFichajeResuelto(preparacion.data!.socio.id)}
            acciones={
              <>
                <button
                  className="boton secundario"
                  onClick={() => setFichajeResuelto(preparacion.data!.socio.id)}
                >
                  {t("m.sinFichar.seguir")}
                </button>
                <button
                  className="boton"
                  disabled={fichando}
                  onClick={async () => {
                    const id = preparacion.data!.socio.id;
                    setFichando(true);
                    try {
                      await api("/accesos", {
                        metodo: "POST",
                        cuerpo: { socioId: id, tipo: "ENTRADA" },
                      });
                      setFichajeResuelto(id);
                      void preparacion.refetch();
                    } catch (fallo) {
                      setError(
                        fallo instanceof Error
                          ? fallo.message
                          : t("m.sinFichar.fallo"),
                      );
                      setFichajeResuelto(id);
                    } finally {
                      setFichando(false);
                    }
                  }}
                >
                  {fichando
                    ? t("m.sinFichar.fichando")
                    : t("m.sinFichar.fichar")}
                </button>
              </>
            }
          >
            <p style={{ marginTop: 0 }}>
              <strong>
                {preparacion.data.socio.nombre}{" "}
                {preparacion.data.socio.apellidos}
              </strong>{" "}
              {t("m.sinFichar.texto")}
            </p>
            <p className="suave pequeno" style={{ marginBottom: 0 }}>
              {t("m.sinFichar.nota")}
            </p>
          </Modal>
        )}

      {avisoLimite && (
        <Modal
          titulo={t("m.limite.titulo")}
          alCerrar={() => setAvisoLimite(null)}
          acciones={
            <>
              <button
                className="boton secundario"
                onClick={() => setAvisoLimite(null)}
              >
                {t("g.cancelar")}
              </button>
              <button
                className="boton peligro"
                onClick={() => {
                  setAvisoLimite(null);
                  void registrar(true);
                }}
              >
                {t("m.limite.autorizar")}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>{avisoLimite}</p>
          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("m.limite.nota")}
          </p>
        </Modal>
      )}

      {venta && (
        <Modal
          titulo={
            venta.numero === null
              ? t("m.venta.enCola")
              : t("m.venta.registrada", { numero: venta.numero })
          }
          alCerrar={() => setVenta(null)}
          acciones={
            <button className="boton" onClick={() => setVenta(null)} autoFocus>
              {t("g.cerrar")}
            </button>
          }
        >
          {venta.socio && (
            <p className="suave pequeno" style={{ marginTop: 0 }}>
              {venta.socio}
            </p>
          )}

          <div className="resumen-venta">
            {venta.lineas.map((l, i) => (
              <div key={i} className="resumen-venta-linea">
                <span>
                  {l.nombre}{" "}
                  <span className="suave">
                    · {formatearCantidad(l.cantidadMg, l.unidad)}
                  </span>
                </span>
                <span className="tabular">{creditos(l.importeCentimos)}</span>
              </div>
            ))}
          </div>

          <div className="resumen-venta-total">
            <span>{t("m.total")}</span>
            <strong className="tabular">{creditos(venta.totalCentimos)}</strong>
          </div>

          <div className="suave pequeno tabular" style={{ textAlign: "right" }}>
            {gramos(venta.totalMg)}
            {venta.totalUnidades > 0 && ` · ${venta.totalUnidades} ud`}
            {" · "}
            {t(`pago.${venta.metodoPago}` as Clave)}
          </div>
          {venta.ahorroCentimos > 0 && (
            <div
              className="pequeno tabular"
              style={{ textAlign: "right", color: "var(--verde)" }}
            >
              {t("m.ahorro", { importe: creditos(venta.ahorroCentimos) })}
            </div>
          )}
          {venta.descuentoManualCentimos > 0 && (
            <div
              className="pequeno tabular"
              style={{ textAlign: "right", color: "var(--verde)" }}
            >
              {t("m.descuentoAMano", {
                importe: creditos(venta.descuentoManualCentimos),
              })}
            </div>
          )}
          {venta.saldoRestanteCentimos !== null && (
            <div className="pequeno tabular" style={{ textAlign: "right" }}>
              {t("m.venta.quedan", { importe: creditos(venta.saldoRestanteCentimos) })}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}


/**
 * Cancelar el pedido de un socio, con su motivo.
 *
 * El motivo se pide y no es opcional: el socio lo lee en su portal, asi que es
 * la diferencia entre «no nos queda Amnesia, pasate y te lo cambiamos» y que le
 * desaparezca el pedido sin saber por que y llame al club a preguntar.
 *
 * Cancelar un pedido no devuelve nada -una reserva no mueve dinero ni stock,
 * solo retiene una peticion-, asi que lo unico que hay que contarle a quien lo
 * hace es lo que va a ver el socio.
 */
function DialogoCancelarPedido({
  reserva,
  alCerrar,
  alHecho,
  alFallar,
}: {
  reserva: ReservaAbierta;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t, delFallo } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("m.can.titulo", { numero: reserva.numero })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton peligro"
            disabled={enviando || motivo.trim().length < 3}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/reservas/${reserva.id}/cancelar`, {
                  metodo: "POST",
                  cuerpo: { motivo },
                });
                alHecho();
              } catch (fallo) {
                alFallar(delFallo(fallo, "m.can.fallo"));
              } finally {
                setEnviando(false);
              }
              // El cuadro se queda abierto con lo escrito dentro: desde aqui se
              // vuelve a intentar sin reescribir nada.
            }}
          >
            {t("m.can.confirmar")}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>{t("m.can.quePasa")}</p>
      <Campo etiqueta={t("m.can.motivo")}>
        {/* El tope es el mismo que el del servidor: sin el, pasarse de largo
            devolvia un error del validador en ingles. Igual que Donaciones. */}
        <textarea
          rows={3}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
        />
      </Campo>
    </Modal>
  );
}
